<template>
  <component
    :is="componentType"
    :href="href"
    :to="to"
    :target="href ? target : undefined"
    :type="!href && !to ? type : undefined"
    :class="buttonClasses"
    :disabled="disabled"
    @click="handleClick"
  >
    <span class="btn-content">
      <slot />
      <v-icon v-if="arrow" small class="arrow-icon">mdi-arrow-right</v-icon>
    </span>
  </component>
</template>

<script>
export default {
  name: 'GradientButton',
  props: {
    variant: {
      type: String,
      default: 'gradient',
      validator: (value) => ['gradient', 'bordered', 'bordered-white', 'solid', 'white'].includes(value)
    },
    size: {
      type: String,
      default: 'medium',
      validator: (value) => ['small', 'medium', 'large'].includes(value)
    },
    href: {
      type: String,
      default: null
    },
    to: {
      type: [String, Object],
      default: null
    },
    target: {
      type: String,
      default: '_blank'
    },
    type: {
      type: String,
      default: 'button'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    arrow: {
      type: Boolean,
      default: false
    },
    fullWidth: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    componentType() {
      if (this.to) return 'router-link'
      if (this.href) return 'a'
      return 'button'
    },
    buttonClasses() {
      return [
        'gradient-btn',
        `gradient-btn--${this.variant}`,
        `gradient-btn--${this.size}`,
        {
          'gradient-btn--full': this.fullWidth,
          'gradient-btn--disabled': this.disabled
        }
      ]
    }
  },
  methods: {
    handleClick(event) {
      if (!this.disabled) {
        this.$emit('click', event)
      }
    }
  }
}
</script>

<style scoped>
.gradient-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
  font-weight: 600;
  text-decoration: none;
  border: none;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 50px;
  overflow: hidden;
  text-transform: none;
  letter-spacing: 0.3px;
  white-space: nowrap;
}

.gradient-btn:focus {
  outline: none;
}

/* Size Variants */
.gradient-btn--small {
  padding: 8px 20px;
  font-size: 13px;
  min-height: 36px;
}

.gradient-btn--medium {
  padding: 12px 32px;
  font-size: 14px;
  min-height: 44px;
}

.gradient-btn--large {
  padding: 14px 40px;
  font-size: 16px;
  min-height: 52px;
}

/* Full Width */
.gradient-btn--full {
  width: 100%;
}

/* Gradient Variant */
.gradient-btn--gradient {
  background: linear-gradient(116deg, #1a1423 0%, #6320ee 100%);
  color: #ffffff;
  border: 2px solid transparent;
}

.gradient-btn--gradient::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(116deg, #2a2433 0%, #7330ff 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 0;
}

.gradient-btn--gradient:hover::before {
  opacity: 1;
}

.gradient-btn--gradient:active {
  transform: translateY(1px);
}

/* Bordered Variant (Purple Gradient Border) */
.gradient-btn--bordered {
  background: transparent;
  color: #ffffff;
  border: 2px solid transparent;
  background-clip: padding-box;
  position: relative;
}

.gradient-btn--bordered::before {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%);
  border-radius: 50px;
  z-index: -1;
}

.gradient-btn--bordered::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  border-radius: 48px;
  z-index: -1;
  transition: background 0.3s ease;
}

.gradient-btn--bordered:hover::after {
  background: rgba(0, 0, 0, 0.3);
}

.gradient-btn--bordered:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(124, 58, 237, 0.4);
}

/* Bordered White Variant */
.gradient-btn--bordered-white {
  background: transparent;
  color: #ffffff;
  border: 2px solid rgba(255, 255, 255, 0.8);
  position: relative;
}

.gradient-btn--bordered-white::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  border-radius: 48px;
  z-index: -1;
  transition: background 0.3s ease;
}

.gradient-btn--bordered-white:hover {
  border-color: rgba(255, 255, 255, 1);
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(255, 255, 255, 0.2);
}

.gradient-btn--bordered-white:hover::after {
  background: rgba(255, 255, 255, 0.15);
}

/* Solid Variant (Purple) */
.gradient-btn--solid {
  background: #7C3AED;
  color: #ffffff;
  border: none;
}

.gradient-btn--solid:hover {
  background: #6D28D9;
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(124, 58, 237, 0.4);
}

.gradient-btn--solid:active {
  transform: translateY(0);
}

/* White Variant */
.gradient-btn--white {
  background: #ffffff;
  color: #1a1423;
  border: 2px solid transparent;
}

.gradient-btn--white:hover {
  background: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
}

.gradient-btn--white:active {
  transform: translateY(0);
}

/* Disabled State */
.gradient-btn--disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

/* Button Content */
.btn-content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 6px;
}

.arrow-icon {
  transition: transform 0.3s ease;
}

.gradient-btn:hover .arrow-icon {
  transform: translateX(4px);
}

/* Responsive */
@media (max-width: 960px) {
  .gradient-btn--small {
    padding: 8px 18px;
    font-size: 12px;
  }

  .gradient-btn--medium {
    padding: 11px 28px;
    font-size: 13px;
  }

  .gradient-btn--large {
    padding: 13px 36px;
    font-size: 15px;
  }
}

@media (max-width: 600px) {
  .gradient-btn--small {
    padding: 7px 16px;
    font-size: 11px;
    min-height: 32px;
  }

  .gradient-btn--medium {
    padding: 10px 24px;
    font-size: 12px;
    min-height: 38px;
  }

  .gradient-btn--large {
    padding: 11px 32px;
    font-size: 14px;
    min-height: 44px;
  }
}

@media (max-width: 400px) {
  .gradient-btn--small {
    padding: 6px 14px;
    font-size: 11px;
    min-height: 30px;
  }

  .gradient-btn--medium {
    padding: 9px 22px;
    font-size: 12px;
    min-height: 36px;
  }

  .gradient-btn--large {
    padding: 10px 28px;
    font-size: 13px;
    min-height: 42px;
  }
}
</style>
